import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import VisibilityIcon from '@mui/icons-material/Visibility';
import HistoryIcon from '@mui/icons-material/History';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import MasterDrawer from './create/MasterDrawer';
import NoDataFound from '../Utils/NoDataFound';
import CustomerHistory from './CustomerHistory';
import PageHeader from '../common/PageHeader';
import SearchField from '../common/SearchField';
import { TabKey, masterConfig, tabOrder } from './masterConfig';
import { useDeleteMasterMutation, useGetMasterPageMutation } from '../../api/master/MasterApi';
import { MasterRow } from '../../types/master';
import { useSnackbar } from '../../utils/snackbar';
import { can } from '../../utils/auth';

const Masters = () => {
  const [currentTab, setCurrentTab] = useState<TabKey>('BRAND');
  const [currentView, setCurrentView] = useState<'DEFAULT' | 'CREATE'>('DEFAULT');
  const [searchValue, setSearchValue] = useState('');
  const [rows, setRows] = useState<MasterRow[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [editData, setEditData] = useState<MasterRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<MasterRow | null>(null);
  const [viewTarget, setViewTarget] = useState<MasterRow | null>(null);
  const [historyId, setHistoryId] = useState<number | null>(null);

  const paginationTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { openSnackbar } = useSnackbar();
  const [getMasterPage] = useGetMasterPageMutation();
  const [deleteMaster, { isLoading: deleting }] = useDeleteMasterMutation();

  const config = masterConfig[currentTab];

  // Buttons follow the permission grid, so staff are not shown things that would
  // only come back refused.
  const mayAdd = can('MASTERS', 'create');
  const mayEdit = can('MASTERS', 'edit');
  const mayDelete = can('MASTERS', 'delete');

  const paginationFunction = (search: string, page: number, size: number, tab: TabKey) => {
    if (paginationTimeout.current) clearTimeout(paginationTimeout.current);
    setLoading(true);
    paginationTimeout.current = setTimeout(async () => {
      try {
        const response = await getMasterPage({ path: masterConfig[tab].path, searchValue: search, page, size }).unwrap();
        setRows(response?.data || []);
        setRowCount(response?.totalCount || 0);
      } catch {
        setRows([]);
        setRowCount(0);
      } finally {
        setLoading(false);
      }
    }, 600);
  };

  useEffect(() => {
    paginationFunction(searchValue, paginationModel.page, paginationModel.pageSize, currentTab);
    // Without this the 600 ms timer fires after the screen is gone and sets state
    // on an unmounted component.
    return () => {
      if (paginationTimeout.current) clearTimeout(paginationTimeout.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, paginationModel.page, paginationModel.pageSize, currentTab]);

  const toggleTab = (_event: React.SyntheticEvent, newValue: TabKey) => {
    setCurrentTab(newValue);
    setSearchValue('');
    setPaginationModel({ page: 0, pageSize: 10 });
  };

  const handleEditData = (data: MasterRow) => {
    setEditData(data);
    setCurrentView('CREATE');
  };

  const createDialog = () => {
    setEditData(null);
    setCurrentView('CREATE');
  };

  const closeDrawer = () => {
    setCurrentView('DEFAULT');
    setEditData(null);
    paginationFunction(searchValue, paginationModel.page, paginationModel.pageSize, currentTab);
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.id) return;
    try {
      await deleteMaster({ path: config.path, id: Number(deleteTarget.id) }).unwrap();
      openSnackbar('Deleted Successfully', 'success');
      paginationFunction(searchValue, paginationModel.page, paginationModel.pageSize, currentTab);
    } catch (error: any) {
      // The backend refuses to delete a brand that still has models, and says why.
      openSnackbar(error?.data?.message || 'Error While Deleting', 'error');
    } finally {
      setDeleteTarget(null);
    }
  };

  const actionColumn: GridColDef = {
    field: 'Action',
    headerName: 'Action',
    flex: 1,
    minWidth: 160,
    align: 'center',
    headerAlign: 'center',
    sortable: false,
    renderCell: (row) => (
      <>
        <IconButton size="small" onClick={() => setViewTarget(row.row)} aria-label="View">
          <VisibilityIcon fontSize="small" />
        </IconButton>
        {/* Only a customer has a story worth opening. */}
        {currentTab === 'CUSTOMER' && (
          <Tooltip title="Purchase history">
            <IconButton size="small" onClick={() => setHistoryId(Number(row.row.id))} aria-label="History">
              <HistoryIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {mayEdit && (
          <IconButton size="small" onClick={() => handleEditData(row.row)} aria-label="Edit">
            <EditTwoToneIcon fontSize="small" />
          </IconButton>
        )}
        {mayDelete && (
          <IconButton size="small" color="error" onClick={() => setDeleteTarget(row.row)} aria-label="Delete">
            <DeleteForeverIcon fontSize="small" />
          </IconButton>
        )}
      </>
    ),
  };

  /** Label and value pairs for the view dialog, straight from the tab's field list. */
  const viewFields = () =>
    config.fields.map((field) => ({
      label: field.label,
      // A select stores an id but shows the name the backend sent alongside it.
      value: field.type === 'select' ? viewTarget?.[field.key.replace('Id', 'Name')] : viewTarget?.[field.key],
    }));

  return (
    <Box p={2} sx={{ width: '100%', minHeight: '85svh' }}>
      <PageHeader title="Masters" subtitle="Brands, models, EMI banks, suppliers and customers.">
        {mayAdd && (
          <Button variant="contained" size="small" onClick={createDialog} startIcon={<PersonAddAlt1Icon />}>
            Create
          </Button>
        )}
      </PageHeader>

      <Tabs value={currentTab} onChange={toggleTab} aria-label="master tabs" variant="scrollable" scrollButtons="auto" sx={{ mb: 1 }}>
        {tabOrder.map((tab) => (
          <Tab key={tab} label={masterConfig[tab].label} value={tab} />
        ))}
      </Tabs>

      <Box sx={{ mb: 1 }}>
        <SearchField value={searchValue} onChange={setSearchValue} placeholder={config.searchPlaceholder} width={300} />
      </Box>

      {rows.length === 0 && !loading ? (
        <NoDataFound
          onCreateClick={mayAdd ? createDialog : () => paginationFunction(searchValue, 0, paginationModel.pageSize, currentTab)}
          buttonName={mayAdd ? 'Create' : 'Refresh'}
          message={`No ${config.label.toLowerCase()} found`}
        />
      ) : (
        <DataGrid
          columns={[...config.columns, actionColumn]}
          rows={rows}
          rowCount={rowCount}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={(model: GridPaginationModel) => setPaginationModel(model)}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          loading={loading}
          sx={{ height: '70vh' }}
        />
      )}

      {currentView === 'CREATE' && <MasterDrawer tab={currentTab} editData={editData} closeDrawer={closeDrawer} />}

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
          <WarningAmberRoundedIcon color="warning" />
          <Typography fontWeight={700} fontSize="1rem">
            Confirm Delete
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to delete{' '}
            <Box component="span" fontWeight={700} color="text.primary">
              {String(deleteTarget?.[config.nameKey] ?? '')}
            </Box>
            ? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button variant="outlined" color="inherit" size="small" onClick={() => setDeleteTarget(null)}>
            No, Cancel
          </Button>
          <LoadingButton variant="contained" color="error" size="small" onClick={confirmDelete} loading={deleting}>
            Yes, Delete
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog open={!!viewTarget} onClose={() => setViewTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
          <VisibilityIcon color="primary" />
          <Typography fontWeight={700} fontSize="1rem">
            View Details
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          {viewFields().map((field) => (
            <Box sx={{ mb: 2 }} key={field.label}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                {field.label}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.25, wordBreak: 'break-word' }}>
                {field.value === null || field.value === undefined || field.value === '' ? '—' : String(field.value)}
              </Typography>
            </Box>
          ))}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button variant="outlined" color="inherit" size="small" onClick={() => setViewTarget(null)}>
            Close
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<EditTwoToneIcon />}
            onClick={() => {
              if (viewTarget) {
                handleEditData(viewTarget);
                setViewTarget(null);
              }
            }}
          >
            Edit
          </Button>
        </DialogActions>
      </Dialog>
      {historyId && <CustomerHistory customerId={historyId} onClose={() => setHistoryId(null)} />}

    </Box>
  );
};

export default Masters;
